import { useLoad } from "@redotech/react-util/load";
import { CustomerCustomFieldDataType } from "@redotech/redo-model/customer-custom-fields/customer-custom-fields-definition";
import { SegmentDataStructureType } from "@redotech/redo-model/marketing/segments/segment-data-structures";
import { CustomerDataSource } from "@redotech/redo-model/marketing/segments/segment-types";
import {
  TokenCompare,
  TokenCompareOperators,
} from "@redotech/redo-model/segments/segment-where-condition";
import { filterTruthy, unique } from "@redotech/util/array";
import { createContext, memo, useContext, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { RedoBadgeColor } from "../../../arbiter-components/badge/redo-badge";
import { RedoTagInputField } from "../../../arbiter-components/input/redo-tag-input-field";
import {
  BadgeRedoListItem,
  RedoListItem,
} from "../../../arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "../../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInput } from "../../../arbiter-components/select-dropdown/redo-tags-dropdown-input";

export interface SegmentTokenOption {
  text: string;
  id: string;
  color?: RedoBadgeColor;
  /**
   * Only used for custom fields, for determining the type of the field
   */
  typeHint?: CustomerCustomFieldDataType;
}

/**
 * We want an object to pass a ==== check on everything except for search string changes
 */
export class SegmentTokenKey {
  static keys = new Map<string, SegmentTokenKey>();

  readonly dataStructure: SegmentDataStructureType;
  readonly dataStructureField: string;
  readonly dataStructureFieldSource?: CustomerDataSource;
  readonly prerequisiteValues?: string[];

  constructor({
    dataStructure,
    dataStructureField,
    dataStructureFieldSource,
    prerequisiteValues,
  }: {
    dataStructure: SegmentDataStructureType;
    dataStructureField: string;
    dataStructureFieldSource?: CustomerDataSource;
    prerequisiteValues?: string[];
  }) {
    this.dataStructure = dataStructure;
    this.dataStructureField = dataStructureField;
    this.dataStructureFieldSource = dataStructureFieldSource;
    this.prerequisiteValues = prerequisiteValues;
    const prerequisiteValuesKey = prerequisiteValues || [];
    const key = filterTruthy([
      dataStructure,
      dataStructureField,
      dataStructureFieldSource,
      ...prerequisiteValuesKey,
    ]).join("ӿ");

    const existing = SegmentTokenKey.keys.get(key);
    if (!existing) {
      SegmentTokenKey.keys.set(key, this);
      return this;
    } else {
      return existing;
    }
  }
}

export type TokenOptionsHandler = (
  token: SegmentTokenKey,
  args: { search: string | undefined },
) => Promise<SegmentTokenOption[][] | null> | SegmentTokenOption[][] | null;
export interface SegmentTokenFetcher {
  getTokenOptions: TokenOptionsHandler;
}

export const SegmentTokenOptionsContext = createContext<SegmentTokenFetcher>({
  getTokenOptions: () => [],
});

const tokenCompareOperatorsToFriendly: Record<TokenCompareOperators, string> = {
  [TokenCompareOperators.NONE]: "is none of",
  [TokenCompareOperators.ANY]: "is any of",
};

const tokenCompareOperatorItems: RedoListItem<TokenCompareOperators>[] =
  Object.values(TokenCompareOperators).map((operator) => ({
    id: operator,
    value: operator,
    text: tokenCompareOperatorsToFriendly[operator],
    type: "text",
  }));

export const TokenFilter = memo(function TokenFilter({
  comparison,
  setComparison,
  dimension,
  fieldSource,
  dataStructure,
  precedingValues,
}: {
  comparison: TokenCompare;
  setComparison: (condition: TokenCompare) => void;
  dimension: string;
  fieldSource?: CustomerDataSource;
  dataStructure: SegmentDataStructureType;
  precedingValues?: string[];
}) {
  return (
    <>
      <RedoSingleSelectDropdownInput<TokenCompareOperators>
        fitToAnchor={false}
        flexGrow={false}
        options={tokenCompareOperatorItems}
        optionSelected={({ item }) => {
          setComparison({ ...comparison, operator: item.value });
        }}
        selectedItem={tokenCompareOperatorItems.find(
          (item) => item.id === comparison.operator,
        )}
        size="xs"
      />
      <MultipleTokenOptionSelect
        dataStructure={dataStructure}
        dimension={dimension}
        fieldSource={fieldSource}
        key={dimension}
        precedingValues={precedingValues}
        selectedValues={comparison.values}
        setSelectedValues={(values) => {
          setComparison({ ...comparison, values });
        }}
      />
    </>
  );
});

export const MultipleTokenOptionSelect = memo(function TokenOptionSelect({
  dimension,
  fieldSource,
  selectedValues,
  setSelectedValues,
  dataStructure,
  precedingValues,
  hierarchyIndex = 0,
  disabled,
  placeholder,
}: {
  dimension: string;
  fieldSource?: CustomerDataSource;
  disabled?: boolean;
  selectedValues: string[];
  setSelectedValues(selectedValues: string[]): void;
  dataStructure: SegmentDataStructureType;
  precedingValues?: string[];
  hierarchyIndex?: number;
  placeholder?: string;
}) {
  const tokenOptionsFetcher = useContext(SegmentTokenOptionsContext);

  const [search, setSearch] = useState<string>("");

  const debouncedSearch = useDebounce(search, 500);

  const tokenOptionsLoad = useLoad(
    async () =>
      tokenOptionsFetcher.getTokenOptions(
        new SegmentTokenKey({
          dataStructure,
          dataStructureField: dimension,
          dataStructureFieldSource: fieldSource,
          prerequisiteValues: precedingValues,
        }),
        { search: debouncedSearch || undefined },
      ),
    [
      tokenOptionsFetcher,
      dimension,
      fieldSource,
      debouncedSearch,
      precedingValues,
    ],
  );

  const loadedTokenOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return (
      (tokenOptionsLoad.value ?? [])[hierarchyIndex]
        ?.slice(0, 100)
        .map<BadgeRedoListItem<string>>((item) => {
          return {
            id: item.id,
            value: item.id,
            type: "badge",
            badge: { text: item.text, color: item.color },
          };
        }) ?? []
    );
  }, [tokenOptionsLoad, hierarchyIndex]);

  const tokenOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    const loadedIds = new Set(loadedTokenOptions.map((option) => option.id));

    const selectedOptionsThatArentLoaded = selectedValues.filter(
      (value) => !loadedIds.has(value),
    );

    const selectedDropdownItemsThatArentLoaded =
      selectedOptionsThatArentLoaded.map<BadgeRedoListItem<string>>(
        (value) => ({
          id: value,
          value,
          type: "badge",
          badge: { text: value },
        }),
      );

    return [...loadedTokenOptions, ...selectedDropdownItemsThatArentLoaded];
  }, [loadedTokenOptions, selectedValues]);

  const selectedOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return selectedValues.map((value) => {
      const option = tokenOptions.find((option) => option.id === value);
      if (option) {
        return option;
      } else {
        return { id: value, value, type: "badge", badge: { text: value } };
      }
    });
  }, [selectedValues, tokenOptions]);

  return tokenOptionsLoad.value === null ? (
    <RedoTagInputField
      delimiters={["comma", "newline"]}
      fitContent
      inputValue={search}
      placeholder="Type a 3-digit area code and press Enter"
      setInputValue={(newValue) => {
        setSearch(
          Array.from(newValue)
            .filter((char) => "0123456789".includes(char))
            .join("")
            .slice(0, 3),
        );
      }}
      setTags={setSelectedValues}
      tags={selectedValues.map((value) => ({ text: value }))}
    />
  ) : (
    <RedoTagsDropdownInput
      disabled={disabled}
      fitToAnchor={false}
      flexGrow={false}
      loading={tokenOptionsLoad.pending}
      options={tokenOptions}
      placeholder={placeholder || "Choose options"}
      searchPlaceholder="Search"
      searchString={search}
      searchSubmitted={(search) => {
        const optionWithName = tokenOptions.find(
          (option) => option.value === search,
        );
        if (optionWithName) {
          setSelectedValues(
            unique([...selectedValues, optionWithName.id as string]),
          );
        } else {
          setSelectedValues(unique([...selectedValues, search]));
        }
      }}
      selectedOptions={selectedOptions}
      setSearchString={setSearch}
      setSelectedOptions={(options) => {
        setSelectedValues(unique(options.map((option) => option.id as string)));
      }}
      size="xs"
    />
  );
});

export const SingleTokenOptionSelect = memo(function TokenOptionSelect({
  dimension,
  disabled,
  selectedValue,
  setSelectedValue,
  dataStructure,
  fieldSource,
  precedingValues,
  hierarchyIndex = 0,
  placeholder,
  flexGrow = true,
}: {
  dimension: string;
  disabled?: boolean;
  selectedValue: string;
  setSelectedValue(selectedValues: string | undefined): void;
  dataStructure: SegmentDataStructureType;
  fieldSource?: CustomerDataSource;
  precedingValues?: string[];
  hierarchyIndex?: number;
  placeholder?: string;
  flexGrow?: boolean;
}) {
  const tokenOptionsFetcher = useContext(SegmentTokenOptionsContext);
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 500);

  const tokenOptionsLoad = useLoad(
    async () =>
      tokenOptionsFetcher.getTokenOptions(
        new SegmentTokenKey({
          dataStructure,
          dataStructureField: dimension,
          dataStructureFieldSource: fieldSource,
          prerequisiteValues: precedingValues,
        }),
        { search: debouncedSearch || undefined },
      ),
    [
      tokenOptionsFetcher,
      dimension,
      fieldSource,
      debouncedSearch,
      precedingValues,
    ],
  );

  const loadedTokenOptions: RedoListItem<string>[] = useMemo(() => {
    return (
      (tokenOptionsLoad.value ?? [])[hierarchyIndex]?.map<RedoListItem<string>>(
        (item) => {
          return { id: item.id, type: "text", value: item.id, text: item.text };
        },
      ) ?? []
    );
  }, [tokenOptionsLoad, hierarchyIndex]);

  const selectedItem: RedoListItem<string> | undefined = useMemo(
    () =>
      selectedValue
        ? {
            id: selectedValue,
            type: "text",
            value:
              loadedTokenOptions.find((option) => option.id === selectedValue)
                ?.value ?? selectedValue,
            text: selectedValue,
          }
        : undefined,
    [selectedValue, loadedTokenOptions],
  );

  return (
    <RedoSingleSelectDropdownInput
      disabled={disabled}
      fitToAnchor={false}
      flexGrow={flexGrow}
      itemsLoading={tokenOptionsLoad.pending}
      options={loadedTokenOptions}
      optionSelected={({ item }) => {
        setSelectedValue(item.value);
      }}
      placeholder={placeholder || "Choose option"}
      searchPlaceholder="Search"
      searchString={search}
      selectedItem={selectedItem}
      setSearchString={setSearch}
      size="xs"
    />
  );
});
